import './styles/job-details.scss'

import React       from 'react'
import PropTypes   from 'prop-types'
import classnames  from 'classnames'
import { connect } from 'react-redux'
import moment      from 'moment'
import filesize    from 'filesize'

import { Card } from 'semantic-ui-react'

import DetailsSection from '../components/DetailsSection'
import DetailsHeader  from '../components/DetailsHeader'
import MetadataView   from '../components/MetadataView'
import Loading        from '../components/Loading'
import ComponentTrace from './ComponentTrace'
import JobFamilyList  from './JobFamilyList'
import RetryButton    from './RetryButton'
import FailButton     from './FailButton'
import JobLogItem     from './JobLogItem'
import JobPayload     from './JobPayload'
import JobId          from './JobId'

import { setDebugLogging } from '../redux/actions/settings'
import { canRetryJob, canFailJob } from '../utils'

import api from '../redux/api'

const LOG_PAGE = 10

function RetryLabel() {
  return (
    <div className="retry-container">
      <div className="retried-placeholder">
        This job has been retried
      </div>
    </div>
  )
}

function formatTime(timestamp) {
  return moment(timestamp).format('YYYY-MM-DD h:mm:ss A')
}

function getTransactionTime(job, logs) {
  let endTime = null

  if (job.status === 'DELIVERED') {
    endTime = job.deliveryTimestamp
  } else if (job.status === 'FAILED' && logs && logs.length > 0) {
    endTime = job.failureTimestamp//getFailureTime({ job, logs })
  }  

  if (endTime) {
    const duration = moment.duration(endTime - job.creationTimestamp, 'milliseconds')

    const start  = moment(job.creationTimestamp)
    const end    = moment(endTime)
    const diff   = end.diff(start)
    const result = moment.utc(diff)

      
    if (duration.asHours() > 1) {
      return `${result.format('h')} hr ${result.format('m')} min ${result.format('s')} sec`
    } else if (duration.asMinutes() > 1) {
      return `${result.format('m')} min ${result.format('s')} sec`
    } else {
      return `${result.format('s')} sec`
    }
  }
}

const initialState = {
  data:    null,
  loading: true,
  logs:    [],
  selectedLog: null,    
}

function StatusDetail({ title, content }) {
  return (
    <div className="status-detail">
      <div className="title">{ title }</div>
      <div className="content">{ content }</div>
    </div>
  )
}

class JobDetails extends React.Component {
  static propTypes = {
    match:           PropTypes.object.isRequired,
    history:         PropTypes.object.isRequired,
    setDebugLogging: PropTypes.func.isRequired,
    showDebugLogs:   PropTypes.bool.isRequired,
    featureFlags:    PropTypes.array.isRequired,
  }

  state = { ...initialState }

  componentDidMount() {
    this.loadJobDetails()
  }

  loadJobDetails() {
    this.refreshJob()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.url !== this.props.match.url) {
      this.clearTimeout()

      this.setState({ ...initialState }, () => {
        this.refreshJob()
      })
    }
  }

  refreshJob() {
    const { id }        = this.props.match.params
    const { logCursor } = this.state
    
    api.loadJob({ id, logCount: LOG_PAGE, logCursor }).then((result) => {
      if (result) {
        const logCursor = (result.logs
          && result.logs.edges
          && result.logs.edges.length > 0
          && result.logs.edges[result.logs.edges.length - 1].cursor) || this.state.logCursor
  
        const oldLogEdges = (this.state.data && this.state.data.logs && this.state.data.logs.edges) || []
        const newLogEdges = (result.logs && result.logs.edges) || []

        const mergedEdges = oldLogEdges.concat(newLogEdges)
        const mergedLogs = result.logs ? { ...result.logs, edges: mergedEdges } : { edges: mergedEdges, pageInfo: { hasNextPage: false } }
        const mergedResult = { ...result, logs: mergedLogs }

        const components = mergedEdges.map(({ node }) => node)

        mergedResult && this.setState({ loading: false, data: mergedResult, components, logCursor }, () => {
          if (mergedLogs.pageInfo && mergedLogs.pageInfo.hasNextPage) {
            this.refreshJob()
          } else {
            this.timeout = setTimeout(this.refreshJob.bind(this), 20000)
          }
        })
      } else {
        this.props.history.push('/')
      }
    })
  }

  componentWillUnmount() {
    this.clearTimeout()
  }

  clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  handleNavigateSftpGatewayLog = (event) => {
    event.preventDefault()
    this.props.history.push(`/sftp-gateway-logs/item/${this.state.data.sftpGatewayLog.id}`)
  }

  handleJob = (job) => {
    this.props.history.push(`/jobs/item/${job.id}`)
  }

  handleJobLogConnectionChange = (connection) => {
    this.jobLogConnection = connection
  }

  handleLogSelected = (selectedLog) => {
    selectedLog = selectedLog === this.state.selectedLog
      ? null
      : selectedLog

    this.setState({ selectedLog })
  }

  filterLogs = ({ node }) => {
    return node === this.state.selectedLog
  }

  removeSelectedLog = (event) => {
    event.preventDefault()
    this.setState({ selectedLog: null })
  }

  toggleDebugLogs = (event) => {
    event.preventDefault()

    const { showDebugLogs, setDebugLogging } = this.props
    setDebugLogging(!showDebugLogs)
  }

  render() {
    const { history, showDebugLogs, featureFlags } = this.props
    const { loading, data, components, selectedLog, refetch } = this.state

    if (loading) return <Loading/>

    const failed    = data.status === 'FAILED'
    const duplicate = data.status === 'DUPLICATE'
    const onhold    = data.status === 'ON_HOLD'
    const cancelled = data.status === 'CANCELLED'

    const retryable = canRetryJob({ job: data })
    const failable  = canFailJob({ job: data })
    
    const timestamp   = formatTime(data.creationTimestamp)
    const statusClass = classnames('status', { success: data.status === 'DELIVERED', failed, duplicate, onhold, cancelled })

    const fileSize = data && data.fileSize > 0 && filesize(data.fileSize)

    const cardGroupItems = [{
      header: 'Creation Time',
      description: formatTime(data.creationTimestamp),
    }]

    if (data.deliveryTimestamp) {
      cardGroupItems.push({
        header:      'Delivery Time',
        description: formatTime(data.deliveryTimestamp),
      })
    } else if (failed && components.length > 0) {
      cardGroupItems.push({
        header:      'Failure Time',
        description: formatTime(data.failureTimestamp),
      })
    } else if (cancelled) {
      cardGroupItems.push({
        header:      'Cancellation Time',
        description: formatTime(data.cancellationTimestamp),
      })
    }

    const filter      = selectedLog && this.filterLogs
    const jobLogTitle = selectedLog
      ? 'Job Log (filtered)'
      : 'Job Log'

    const unfiltered = ((data && data.logs && data.logs.edges) || []).slice().reverse()
    const edges      = filter
      ? unfiltered.filter(filter)
      : unfiltered

    const hasParents     = data.parents && data.parents.length > 0
    const hasChildren    = data.children && data.children.length > 0
    const hasBatchedJobs = data.batchedJobs && data.batchedJobs.length > 0

    const retryFromTitle = data.retriedFrom && (
      data.retriedFrom.integration
        ? data.retriedFrom.integration.name
        : formatTime(data.retriedFrom.creationTimestamp)
    )

    const transactionTime = getTransactionTime(data, components)

    const metadata = data && data.metadata
      ? (typeof data.metadata === 'string' ? JSON.parse(data.metadata) : data.metadata)
      : null

    const nextActionTime    = onhold && data.nextActionTime
    const nextActionMessage = nextActionTime && moment.duration(new Date(nextActionTime).getTime() - new Date().getTime()).humanize()
        
    const toggleDebugMessage = showDebugLogs
      ? 'Hide debug logs'
      : 'Show debug logs'

    const hasPayloadHistory = data
      && Array.isArray(data.payloadHistory)
      && data.payloadHistory.length > 0
    const hasStagedPayloadUrl = data && data.stagedPayloadUrl
    const showPayload = hasPayloadHistory || hasStagedPayloadUrl

    return (
      <div className="job-details">
        <div className="header-row">
          <div>
            <DetailsHeader title="Job Details" type={'Job'} description={timestamp} />
            { showPayload && <JobPayload job={data} /> }
          </div>

          <div className={statusClass}>
            <div className="status-container">

              <div className="status-details">
                { transactionTime && <StatusDetail title="Transaction Time" content={transactionTime} /> }
                { fileSize        && <StatusDetail title="Payload Size"     content={fileSize} />        }
              </div>
            
              <div className="item-highlight ui mini message">              
                <div className="title">Status</div>
                <div className="message">{refetch ? 'Loading...' : data.status}</div>

                {
                  nextActionMessage && (
                    <div className="hint">
                      <div className="title">Time remaining on hold</div>
                      <div className="message">{ nextActionMessage }</div>                      
                    </div>
                  )
                }
              </div>

              { retryable && <RetryButton job={data} history={history} forceRetry={true} /> }
              { data.retries.length ? RetryLabel() : "" }
              { failable  && <FailButton  job={data} history={history} /> }
            </div>
      
          </div>
        </div>

        <Card.Group items={cardGroupItems} />

        <div className="details">
          <JobId id={data.id} />

          {
            data.dataSource && (
              <DetailsSection
                title="Data Source"
                name={data.dataSource.name}
                description={data.dataSource.description}
                url={`/data-sources/item/${data.dataSource.id}`}
              />
            )
          }
          {
            data.integration && (
              <DetailsSection
                title="Integration"
                name={data.integration.name}
                description={data.integration.description}
                url={`/integrations/item/${data.integration.id}`}
              />
            )
          }
          {
            data.sender && (
              <DetailsSection
                title="Sender"
                name={data.sender.name}
                description={data.sender.description}
                url={`/data-systems/item/${data.sender.id}`}
              />
            )
          }
          {
            data.receiver && (
              <DetailsSection
                title="Receiver"
                name={data.receiver.name}
                description={data.receiver.description}
                url={`/data-systems/item/${data.receiver.id}`}
              />
            )
          }
          {
            data.dataType && (
              <DetailsSection
                title="Data Type"
                name={data.dataType.name}
                description={data.dataType.description}
                url={`/data-types/item/${data.dataType.id}`}
              />
            )
          }
        </div>

        {
          (hasParents || hasChildren || hasBatchedJobs) && (
            <div className="job-family">
              {
                hasParents && (
                  <JobFamilyList
                    title="Parent Jobs"
                    jobs={data.parents}
                    history={history}
                    onClick={this.handleJob}
                  />
                ) 
              }
              {
                hasChildren && (
                  <JobFamilyList
                    title="Child Jobs"
                    jobs={data.children}
                    history={history}
                    onClick={this.handleJob}
                  />
                )
              }
              {
                hasBatchedJobs && (
                  <JobFamilyList
                    title="Batched Jobs"
                    jobs={data.batchedJobs}
                    history={history}
                    onClick={this.handleJob}
                  />
                )
              }
            </div>  
          )
        }

        {
          data.retries && data.retries.length > 0 && (
            <div className="job-family">
              <JobFamilyList
                title="Retried Jobs"
                jobs={data.retries}
                history={history}
                onClick={this.handleJob}
              />    
            </div>
          )
        }

        {
          data.retriedFrom && (
            <DetailsSection
              title="Retried From"
              name={retryFromTitle}
              url={`/jobs/item/${data.retriedFrom.id}`}
            />
          )
        }

        {
          data.sftpGatewayLog && (
            <div className="links">
              <a href="/" onClick={this.handleNavigateSftpGatewayLog}>SFTP Gateway Log</a>
            </div>
          )
        }

        {
          metadata && <MetadataView metadata={metadata} title="Metadata" />
        }        
        
        <ComponentTrace job={data} logs={components} selectedLog={selectedLog} onLogSelected={this.handleLogSelected} />

        <div className="job-logs">
          <div className="header">
            <div className="title">{ jobLogTitle }</div>

            {
              selectedLog && (
                <div className="filter-link">
                  <a href="/" onClick={this.removeSelectedLog}>Show full log</a>
                </div>  
              )
            }

            {
              featureFlags.includes('enableMonitorDebugLogging') && (
                <div className="toggle-debug">
                  <a href="/" onClick={this.toggleDebugLogs}>{ toggleDebugMessage }</a>
                </div>
              )
            }
          </div>

          <div>
            {
              edges.map(({ cursor, node }) => {
                return <JobLogItem key={`${node.id}-${cursor}`} component={node.component} businessLog={node.businessLog} showDebugLogs={showDebugLogs} />
              })
            }
          </div>
        </div>
      </div>
    )
  }
}

const mapDispatchToProps = {
  setDebugLogging,
}
const mapStateToProps = (state) => ({
  showDebugLogs: state.settings.showDebugLogs,
  featureFlags:  state.features.featureFlags,
})

export default connect(mapStateToProps, mapDispatchToProps)(JobDetails)
